import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Loader2, Search } from "lucide-react";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { initials } from "@/lib/format";
import { useStartConversation } from "@/hooks/useDirectMessages";
import type { MentionableUser } from "@/hooks/useForum";

// ---------------------------------------------------------------------------
// StartConversationDialog — picker for "who do you want to chat with?".
// Reuses /api/v1/users/mentionable/ since it already returns a flat,
// searchable, active-user list (the @-mention popup uses it too).
//
// Picking a row → POSTs /conversations/ {user_id} (idempotent: returns
// the existing convo if one already exists) → navigates straight to
// /messages/<id>. The Messages page then loads the conversation.
// ---------------------------------------------------------------------------
export function StartConversationDialog({
  open, onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const start = useStartConversation();

  const { data: users, isFetching } = useQuery<MentionableUser[]>({
    enabled: open,
    queryKey: ["users", "mentionable", query],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (query) params.set("q", query);
      const { data } = await api.get<MentionableUser[]>(
        `/users/mentionable/?${params.toString()}`,
      );
      return data;
    },
    staleTime: 30_000,
  });

  function pick(user: MentionableUser) {
    start.mutate(user.id, {
      onSuccess: (conv) => {
        onOpenChange(false);
        setQuery("");
        navigate(`/messages/${conv.id}`);
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Start a conversation</DialogTitle>
        </DialogHeader>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name or email…"
            className="pl-9"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </div>

        <ul className="max-h-[50vh] space-y-0.5 overflow-y-auto pr-1">
          {isFetching && !users ? (
            <li className="flex items-center gap-2 px-2 py-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />Loading…
            </li>
          ) : !users || users.length === 0 ? (
            <li className="px-2 py-4 text-center text-sm text-muted-foreground">
              No people {query ? "match" : "found"}.
            </li>
          ) : (
            users.map((u) => (
              <li key={u.id}>
                <button
                  type="button"
                  onClick={() => pick(u)}
                  disabled={start.isPending}
                  className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition hover:bg-accent/50 disabled:opacity-50"
                >
                  <Avatar className="h-9 w-9 ring-1 ring-border">
                    <AvatarFallback className="bg-gradient-to-br from-primary to-emerald-700 text-xs font-semibold text-primary-foreground">
                      {initials(u.full_name || u.email)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{u.full_name || u.email}</div>
                    {u.full_name && (
                      <div className="truncate text-xs text-muted-foreground">{u.email}</div>
                    )}
                  </div>
                  {start.isPending && (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                </button>
              </li>
            ))
          )}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
